import React, { useEffect, useMemo, useRef, useState, FC } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { setUser, fetchMyHerds, logoutUser } from './reducers/userReducer';
import { fetchPosts, refreshPosts } from './reducers/postReducer';
import storageService from './utils/localStorage';
import { setUnauthorizedHandler } from './utils/authEvents';
import { setHerdList, setTopHerdsList } from './reducers/herdReducer';
import { notify } from './reducers/notificationReducer';
import { useNavigate } from 'react-router-dom';
import NavBar from './components/NavBar';
import WebSideNav from './components/WebSideNav';
import ActivityTracker from './components/ActivityTracker';
import AuroraBackground from './components/AuroraBackground';
import MobileAppLayout from './components/Mobile/MobileAppLayout';
import ToastNotif from './components/ToastNotif';
import Routes from './Routes';
import getErrorMsg from './utils/getErrorMsg';
import { isNativeMobileApp } from './utils/platformDetector';
import { initializeSafeArea, watchForStatusBarReset, syncStatusBarTheme } from './utils/safeAreaDetector';
import { startViewportHeightSync } from './utils/viewportHeight';
import { useScrollTopOnPush } from './utils/useScrollRestoration';
import posthog from './utils/posthog';
import {
  SharedContent,
  isSharedContentAvailable,
} from './utils/sharedContent';

import { Box, Paper } from '@mui/material/';
import { getTheme } from './styles/customTheme';
import { ThemeProvider, StyledEngineProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { initThemePreference, type ThemePreference } from './reducers/themeReducer';

const prefersDarkQuery = '(prefers-color-scheme: dark)';

const getSystemDark = (): boolean =>
  typeof window !== 'undefined' && typeof window.matchMedia === 'function'
    ? window.matchMedia(prefersDarkQuery).matches
    : false;

const WebAppComponent: FC = () => {
  // The scroll area persists across routes, so forward navigations must reset
  // it to the top (back navigations restore per-page via useScrollRestoration).
  const [scrollEl, setScrollEl] = useState<HTMLElement | null>(null);
  useScrollTopOnPush(scrollEl);

  return (
    <Paper
      elevation={0}
      sx={{
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
        height: 'var(--layout-viewport-height)',
        backgroundColor: 'background.default',
      }}>
      <AuroraBackground />
      <NavBar />
      {/* Body starts at the very top so content scrolls behind the fixed glass bar;
          the rail and scroll area are offset down by the bar height. */}
      <Box sx={{ flex: 1, display: 'flex', minHeight: 0, position: 'relative', zIndex: 1 }}>
        <WebSideNav mode="rail" />
        <Box
          ref={setScrollEl}
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            minHeight: 0,
            overflowY: 'auto',
            pt: 'var(--layout-top-bar-height)',
          }}
        >
          <Routes />
        </Box>
      </Box>
    </Paper>
  );
};

const App: FC = () => {
  const dispatch: any = useDispatch();
  const navigate = useNavigate();
  const themePreference = useSelector((state: any) => state.themePreference as ThemePreference);
  const userId = useSelector((state: any) => state.user?.id as string | undefined);
  // Track the OS theme so the 'system' preference reacts live to OS changes.
  const [systemDark, setSystemDark] = useState<boolean>(() => getSystemDark());
  const darkMode = themePreference === 'system' ? systemDark : themePreference === 'dark';
  // Resolve layout on first render so we don't flash wrong layout or re-render (faster boot)
  const [isMobileApp] = useState(() => isNativeMobileApp());

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
      return undefined;
    }
    const mq = window.matchMedia(prefersDarkQuery);
    const handler = (event: MediaQueryListEvent) => setSystemDark(event.matches);
    mq.addEventListener?.('change', handler);
    return () => mq.removeEventListener?.('change', handler);
  }, []);

  // Defer safe area init so it doesn't block first paint (run after layout is shown)
  useEffect(() => {
    if (!isMobileApp) {
      return undefined;
    }

    initializeSafeArea();
    // The status bar config is set once above, but native fullscreen and app
    // backgrounding can drop it — watch for those and re-apply.
    return watchForStatusBarReset();
  }, [isMobileApp]);

  // Not gated on isMobileApp: an on-screen keyboard is a mobile-web problem too,
  // and where there is no visualViewport this is a no-op.
  useEffect(() => startViewportHeightSync(), []);

  useEffect(() => {
    if (!isMobileApp || !isSharedContentAvailable()) return undefined;
    let removeListener: (() => Promise<void>) | undefined;

    // Native retains share events until JS subscribes, so this also handles
    // cold-start shares without polling on every app mount.
    SharedContent.addListener('sharedContentReceived', () => {
      navigate(`/submit?share=${Date.now()}`);
    })
      .then((listener) => {
        removeListener = listener.remove;
      })
      .catch(() => {});

    return () => {
      removeListener?.();
    };
  }, [isMobileApp, navigate]);

  // Clear a dead session when an authed request comes back 401 (expired token,
  // or a token issued by a different backend). Guarded so it only fires while
  // actually logged in — avoids redundant logouts on concurrent 401s.
  useEffect(() => {
    setUnauthorizedHandler(() => {
      if (storageService.loadUser()) {
        dispatch(logoutUser());
        dispatch(notify('Your session has expired. Please log in again.', 'error'));
      }
    });
    return () => setUnauthorizedHandler(null);
  }, [dispatch]);

  useEffect(() => {
    const abortController = new AbortController();
    
    const setPostsAndHerds = async () => {
      try {
        // Track session start
        posthog.capture('session_started');
        dispatch(setUser());
        dispatch(initThemePreference());
        // Load initial data in parallel with error handling
        const initialLoadPromises = [
          dispatch(fetchPosts(abortController.signal))
            .catch((err: any) => dispatch(notify('Failed to load posts', 'error'))),
          dispatch(setHerdList())
            .catch((err: any) => dispatch(notify('Failed to load herds', 'error'))),
          dispatch(setTopHerdsList())
            .catch((err: any) => dispatch(notify('Failed to load top herds', 'error')))
        ];
        // Refresh joined-herd ids (single source of truth for membership)
        if (storageService.loadUser()) {
          initialLoadPromises.push(dispatch(fetchMyHerds()).catch(() => {}));
        }
        await Promise.allSettled(initialLoadPromises);
      } catch (err: any) {
        if (!abortController.signal.aborted) {
          dispatch(notify(getErrorMsg(err), 'error'));
        }
      }
    };

    setPostsAndHerds();
    return () => abortController.abort();
  }, [dispatch]);

  /**
   * Re-fetch the feed when the signed-in identity changes.
   *
   * The feed is personalized server-side now, so logging in or out changes what
   * it should contain. Without this it would keep showing the anonymous global
   * list until a full reload.
   */
  const hydratedIdRef = useRef<string | undefined>(storageService.loadUser()?.id);
  const prevUserIdRef = useRef<string | undefined>(undefined);
  useEffect(() => {
    const prev = prevUserIdRef.current;
    prevUserIdRef.current = userId;
    if (prev === userId) return;
    // Boot: the store catching up to what localStorage already said. The boot
    // fetch above was already authenticated, so refetching here would
    // double-load the feed on every launch.
    if (prev === undefined && userId === hydratedIdRef.current) return;
    dispatch(refreshPosts()).catch(() => {});
  }, [dispatch, userId]);

  // Memoize theme creation so we don't rebuild the theme on every render
  const memoizedTheme = useMemo(() => getTheme(darkMode ? 'dark' : 'light'), [darkMode]);

  useEffect(() => {
    document.body.classList.toggle('dark', darkMode);
  }, [darkMode]);

  // Keep the Android status bar (icon contrast + background) in step with the
  // resolved theme — covers manual switches and live OS changes on 'system'.
  useEffect(() => {
    if (!isMobileApp) return;
    syncStatusBarTheme(darkMode, memoizedTheme.palette.background.default).catch(() => {});
  }, [isMobileApp, darkMode, memoizedTheme]);

  const AppComponent = isMobileApp ? MobileAppLayout : WebAppComponent;

  return (
    <StyledEngineProvider injectFirst>
      <ThemeProvider theme={memoizedTheme}>
        <CssBaseline />
        <ToastNotif />
        <ActivityTracker />
        <AppComponent />
      </ThemeProvider>
    </StyledEngineProvider>
  );
};

export default App;
